import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, X, SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandLogo } from "@/components/BrandLogo";
import { BrandStack } from "@/components/BrandStack";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "@/lib/brand-tokens";
import { deals, brands, getBrandById } from "@/lib/mock-data";
import { Progress } from "@/components/ui/progress";

// MEM-02 — Browse deals (member view, with absolute cap remaining)
export default function MemberDealsPage() {
  const active = useMemo(() => deals.filter((d) => d.status === "Published"), []);
  const [brandFilter, setBrandFilter] = useState([]);
  const [rateRange, setRateRange] = useState([85, 100]);
  const [sort, setSort] = useState("rate");
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    // Normalize: lowercase + strip everything except letters/digits/whitespace
    // so "lowes" matches the "Lowe's" brand and "homedepot" matches "Home Depot".
    const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, "");
    const nq = norm(q);
    let list = active.filter((d) => {
      if (brandFilter.length) {
        const dealBrands = d.brandIds || [d.brandId];
        if (!dealBrands.some((bid) => brandFilter.includes(bid))) return false;
      }
      if (d.rate < rateRange[0] || d.rate > rateRange[1]) return false;
      if (nq) {
        // Search name + every accepted brand's name AND aliases
        const dealBrands = (d.brandIds || [d.brandId]).map(getBrandById).filter(Boolean);
        const haystackParts = [
          d.name,
          ...dealBrands.flatMap((b) => [b.name, ...(b.aliases || [])]),
        ];
        const haystack = norm(haystackParts.join(" "));
        if (!haystack.includes(nq)) return false;
      }
      return true;
    });
    if (sort === "rate") list.sort((a, b) => b.rate - a.rate);
    if (sort === "deadline") list.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
    if (sort === "newest") list.sort((a, b) => new Date(b.openedAt) - new Date(a.openedAt));
    return list;
  }, [active, brandFilter, rateRange, sort, q]);

  const toggleBrand = (id) => setBrandFilter((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  return (
    <div data-testid="member-deals-page" className="space-y-6 pb-10">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Inventory</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Browse deals</h1>
          <p className="text-body-sm text-ink-muted mt-1">{filtered.length} of {active.length} live deals match your filters.</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-surface border border-line rounded-xl p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-ink-hint pointer-events-none" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search deal name…" className="pl-8 h-10" data-testid="member-deals-search" />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[200px] h-10">
              <SlidersHorizontal className="size-3.5 mr-2 text-ink-muted" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="rate">Highest rate</SelectItem>
              <SelectItem value="deadline">Soonest deadline</SelectItem>
              <SelectItem value="newest">Newest</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 pt-1">
          <div>
            <div className="flex items-center justify-between text-caption font-semibold text-ink mb-2">
              <span>Rate range</span>
              <span className="tabular-nums text-ink-muted">{rateRange[0]}% &ndash; {rateRange[1]}%</span>
            </div>
            <Slider min={85} max={100} step={1} value={rateRange} onValueChange={setRateRange} />
          </div>
          <div>
            <div className="flex items-center justify-between text-caption font-semibold text-ink mb-2">
              <span>Brands {brandFilter.length > 0 && <span className="text-ink-subtle font-normal">— {brandFilter.length} selected</span>}</span>
              {brandFilter.length > 0 && (
                <button onClick={() => setBrandFilter([])} data-testid="clear-brand-filters"
                        className="text-caption text-ink-muted hover:text-ink underline-offset-2 hover:underline">
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {brands.map((b) => {
                const isActive = brandFilter.includes(b.id);
                // Count published deals that ACCEPT this brand (multi-brand aware).
                const count = active.filter((d) => (d.brandIds || [d.brandId]).includes(b.id)).length;
                if (count === 0) return null; // hide brands with no live deals
                return (
                  <button key={b.id} onClick={() => toggleBrand(b.id)}
                          data-testid={`brand-filter-${b.id}`}
                          className={`px-2.5 py-1 rounded-full border text-caption font-medium transition-all inline-flex items-center gap-1.5 ${
                            isActive ? "bg-brand-700 border-brand-700 text-white" : "bg-surface border-line text-ink-muted hover:border-ink hover:text-ink"
                          }`}>
                    {b.name}
                    <span className={`tabular-nums ${isActive ? "text-white/80" : "text-ink-hint"}`}>{count}</span>
                    {isActive && <X className="size-3" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((deal) => <MemberDealCard key={deal.id} deal={deal} />)}
      </div>
    </div>
  );
}

function MemberDealCard({ deal }) {
  const brand = getBrandById(deal.brandId);
  const days = Math.max(0, Math.ceil((new Date(deal.deadline) - Date.now()) / 86_400_000));
  const fillPct = Math.round((deal.capUsed / deal.capTotal) * 100);
  const remaining = deal.capTotal - deal.capUsed;
  // For multi-brand deals where rates differ, show a range; otherwise single rate.
  const rates = (deal.brandConfigs || []).map(c => c.rate);
  const rateLabel = !deal.ratesUniform && rates.length > 1
    ? `${Math.min(...rates)}–${Math.max(...rates)}`
    : `${deal.rate}`;

  return (
    <Link
      to={`/member/deals/${deal.id}`}
      data-testid={`deal-card-${deal.id}`}
      className="block bg-surface border border-line rounded-xl p-6 transition-all hover:border-brand-700 hover:shadow-md group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
    >
      <div className="flex items-start justify-between">
        <BrandStack brandIds={deal.brandIds} size="md" max={3} />
        <div className="text-right">
          <div className="font-brand text-[28px] font-bold text-brand-700 leading-none tabular-nums">{rateLabel}%</div>
          <div className="text-caption text-ink-subtle">{deal.ratesUniform ? "of face" : "rate range"}</div>
        </div>
      </div>
      <h3 className="mt-5 text-h3 text-ink font-semibold flex items-center gap-2">
        <span className="truncate">{deal.name}</span>
        {deal.isMultiBrand && <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-50 border border-brand-100 px-1.5 py-0.5 rounded shrink-0">Multi</span>}
      </h3>
      <div className="mt-1 text-caption text-ink-subtle truncate">
        {deal.isMultiBrand ? `${deal.brandIds.length} brands` : brand?.name}
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-y-3 text-caption">
        <div>
          <dt className="text-ink-hint">Face value</dt>
          <dd className="text-ink font-semibold tabular-nums">${deal.minFace}&ndash;${deal.maxFace}</dd>
        </div>
        <div>
          <dt className="text-ink-hint">Per-member cap</dt>
          <dd className="text-ink font-semibold tabular-nums">{format.currency(deal.perMemberCap)}</dd>
        </div>
        <div>
          <dt className="text-ink-hint">Closes in</dt>
          <dd className="text-ink font-semibold tabular-nums">{days} day{days === 1 ? "" : "s"}</dd>
        </div>
        <div>
          <dt className="text-ink-hint">Payment terms</dt>
          <dd className="text-ink font-semibold text-caption">{deal.paymentTerms}</dd>
        </div>
      </dl>

      <div className="mt-5">
        <div className="flex items-center justify-between text-caption mb-1.5">
          <span className="text-ink-muted">Cap remaining</span>
          <span className="text-ink font-semibold tabular-nums">{format.currency(remaining)} of {format.currency(deal.capTotal)}</span>
        </div>
        <Progress value={fillPct} className="h-1.5" />
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-caption text-ink-muted group-hover:text-brand-700 transition-colors inline-flex items-center gap-1">
          View details
          <ArrowUpRight className="size-3.5" strokeWidth={2.4} />
        </span>
        <Button
          className="bg-brand-700 hover:bg-brand-800 text-white"
          data-testid={`reserve-btn-${deal.id}`}
          onClick={(e) => {
            // Reserve is the fast-path; clicking it still navigates (the whole card
            // is the link), but stop propagation so we can wire alternate behaviour later.
          }}
        >
          Reserve
          <ArrowUpRight className="size-4 ml-1.5" strokeWidth={2.4} />
        </Button>
      </div>
    </Link>
  );
}
